import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  LineChartPro,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts-pro";
import { useSearchParams } from "react-router-dom";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../../helpers/funcs/widgetMetricMap";
import { getCommonParams } from "../../helpers/funcs/getCommonParams";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../features/userSlice";
import store from "../../../../../../store";
import { trendsService } from "../../../../../../services/api/trends.endpoints";
import GraphSkeleton from "../../helpers/components/GraphSkeleton";
import { format } from "date-fns";
import { subDays, subWeeks, subMonths } from "date-fns";
import { CustomTooltipBDPopup } from "../../helpers/components/CustomTooltipBDPopup";
interface TrendsBreakdownPopupProps {
  open: boolean;
  onClose: () => void;
  data: {
    id: any;
    source: any;
  } | null;
}

const TrendsBreakdownPopupForBars: React.FC<TrendsBreakdownPopupProps> = ({
  open,
  onClose,
  data,
}) => {
  const { selectedTimeUnit } = useSelector((state: any) => state.trends);
  const [timeFrame, setTimeFrame] = useState("last_28_days");
  const [searchParams] = useSearchParams();
  const fullWidgetName = searchParams.get("full_widget_name") || "";
  const widgetName = searchParams.get("widget_name") || "";
  const pointDate = searchParams.get("point_date") || "";
  const metricName = searchParams.get("metric_name") || "";
  const sectionName = searchParams.get("section") || "";
  const queryIDParam = searchParams.get("queryID") || "";
  const valueSuffix = searchParams.get("value_suffix") || "";
  const { userData } = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const [loading, setLoading] = useState(false);
  const [dataPopup, setDataPopup] = useState([]);
  const mappedMetric =
    funnelRatioMetricMap[metricName] ||
    profitAndFirstTimeDepositMetricMap[metricName] ||
    "";
  const mappedWidget = widgetMetricMap[fullWidgetName] || "";
  const filteredWidgetKey = mappedMetric ? mappedMetric : mappedWidget;

  const formatPointDate = (rawDate: string): string => {
    const [day, month, year] = rawDate.split("-");
    if (!day || !month || !year) return rawDate;

    const date = new Date(`${year}-${month}-01`);
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const headerName = widgetName ? widgetName : fullWidgetName;
  const timeFrameOptions = [
    { label: "Last 12 months", value: "last_12_months" },
    { label: "Last 6 weeks", value: "last_6_weeks" },
    { label: "Last 28 days", value: "last_28_days" },
  ];
  const sectionKeyMap: Record<string, string> = {
    Country: "country",
    "Traffic Source": "traffic_source",
    "Ads (Campaign)": "campaign_name",
    "Ads (Ad Group)": "adgroup_name",
    "Keyword Match Type Table": "keyword_id",
    "Brands Table": "brand_id",
  };
  const getTimeFrameRange = (timeFrame: string) => {
    const today = new Date();
    let startDate = today;
    let timeUnit: "day" | "week" | "month" = "day";

    switch (timeFrame) {
      case "last_12_months":
        startDate = subMonths(today, 12);
        timeUnit = "month";
        break;
      case "last_6_weeks":
        startDate = subWeeks(today, 6);
        timeUnit = "week";
        break;
      case "last_28_days":
      default:
        startDate = subDays(today, 28);
        timeUnit = "day";
        break;
    }

    return {
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(today, "yyyy-MM-dd"),
      timeUnit,
    };
  };

  const fetchBreakdownBars = async () => {
    setLoading(true);
    try {
      const { type, website_shared_uid, dimensions } = await getCommonParams({
        searchParams,
        userData,
        dispatch,
        oauthTokenHeader,
        store,
      });
      const { startDate, endDate, timeUnit } = getTimeFrameRange(timeFrame);
      const baseParams = {
        oauthTokenHeader,
        startDate,
        endDate,
        timeUnit,
        type,
        websiteSharedUids: website_shared_uid,
        dimensions,
        campaignId: "1",
        adgroupId: "1",
        matchtype: "all",
        kwId: "1",
        country: "all",
        trafficSource: "all",
      };

      const response = await trendsService.getTrendsBreakdownExecuteQuery({
        ...baseParams,
        queryId: queryIDParam,
      });

      setDataPopup(response?.data?.data?.query_output || []);
    } catch (e: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || e?.message || "Traffic source fetch failed",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && queryIDParam) {
      fetchBreakdownBars();
    }
    // eslint-disable-next-line
  }, [open, queryIDParam, timeFrame]);
  useEffect(() => {
    if (!open) {
      setDataPopup([]);
      setTimeFrame("last_28_days");
    }
  }, [open]);

  const sanetizingData = useMemo(() => {
    if (!dataPopup || !filteredWidgetKey || !open || !data?.source) return [];
    const filterKey = sectionKeyMap[sectionName];
    const filterValue =
      filterKey?.includes("_id") && data?.id ? data.id : data?.source;

    const filtered =
      filterKey && filterValue !== undefined
        ? dataPopup.filter((item: any) => item?.[filterKey] === filterValue)
        : dataPopup;

    const aggregatedMap: Record<string, number> = {};

    filtered.forEach((item: any) => {
      const date = new Date(item?.time_unit);
      const rawData = item?.[filteredWidgetKey];

      if (typeof rawData !== "number" || isNaN(rawData)) return;

      const yValue = +(
        (rawData || 0) * (valueSuffix === "%" ? 100 : 1)
      ).toFixed(2);

      if (isNaN(yValue)) return;

      const formattedX =
        selectedTimeUnit.label === "month"
          ? format(date, "MMMM yyyy")
          : format(date, "dd-MM-yyyy");

      if (!aggregatedMap[formattedX]) {
        aggregatedMap[formattedX] = 0;
      }
      aggregatedMap[formattedX] += yValue;
    });

    return Object.entries(aggregatedMap).map(([x, y]) => ({
      x,
      y: parseFloat(y.toFixed(2)),
    }));
    // eslint-disable-next-line
  }, [
    dataPopup,
    selectedTimeUnit.label,
    filteredWidgetKey,
    sectionName,
    data,
    open,
  ]);

  const sortedData: any = sanetizingData
    ?.filter((d: any) => d !== null)
    .sort((a: any, b: any) => {
      let dateA, dateB;

      if (selectedTimeUnit.label === "month") {
        dateA = new Date(a.x);
        dateB = new Date(b.x);
      } else {
        dateA = new Date(a.x.split("-").reverse().join("-"));
        dateB = new Date(b.x.split("-").reverse().join("-"));
      }

      return dateA.getTime() - dateB.getTime();
    });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 450,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" sx={{ width: "60%" }}>
          {headerName} {mappedMetric ? `(${mappedMetric})` : ""} -{" "}
          {formatPointDate(pointDate)} - {sectionName} Breakdown: {data?.source}
        </Typography>
        <Autocomplete
          disabled={loading}
          size="small"
          options={timeFrameOptions}
          getOptionLabel={(option) => option.label}
          sx={{ width: 272 }}
          value={
            timeFrameOptions.find((opt) => opt.value === timeFrame) || null
          }
          onChange={(_, newValue) => {
            if (newValue) setTimeFrame(newValue.value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Time Frame"
              color="secondary"
            />
          )}
        />
      </DialogTitle>
      {dataPopup ? (
        <Box sx={{ py: 2 }}>
          {loading ? (
            <GraphSkeleton />
          ) : (
            <>
              <LineChartPro
                dataset={sortedData}
                series={[
                  {
                    type: "line",
                    curve: "linear",
                    dataKey: "y",
                    yAxisKey: filteredWidgetKey,
                    color: "#8979FF", // optional
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "point",
                    dataKey: "x",
                    disableLine: true,
                  },
                ]}
                yAxis={[
                  {
                    id: filteredWidgetKey,
                    dataKey: "y",
                    disableLine: true,
                    disableTicks: true,
                    position: "right" as const,
                  },
                ]}
                height={380}
                margin={{ left: 48, right: 56, top: 40, bottom: 40 }}
                sx={{
                  [`& .MuiChartsAxis-tickLabel`]: {
                    fill: "grey !important", // Label color for X and Y axes
                  },
                  [`& .MuiChartsAxis-tick`]: {
                    stroke: "grey !important", // Tick color for X and Y axes
                  },
                  [`& .${lineElementClasses.root}`]: {
                    strokeWidth: 2,
                  },
                  [`& .${markElementClasses.root}`]: {
                    scale: "0.6",
                    strokeWidth: 2,
                    "&:hover": {
                      scale: "1.2",
                      strokeWidth: 5,
                      opacity: "0.6",
                    },
                  },
                }}
                leftAxis={null}
                rightAxis={filteredWidgetKey}
              >
                <ChartsGrid horizontal />

                <CustomTooltipBDPopup valueSuffix={valueSuffix} />

                <ChartsXAxis />
                <ChartsYAxis />

                <LinePlot />
                <MarkPlot />
                <ChartsLegend direction="row" />
              </LineChartPro>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography>No data available</Typography>
        </Box>
      )}

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="secondary"
          // disabled={loading}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendsBreakdownPopupForBars;
